import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  LayoutDashboard, 
  Package, 
  ShoppingCart, 
  FileText, 
  MessageSquare,
  LogOut,
  Menu,
  X,
  ChevronRight
} from 'lucide-react';

const navItems = [
  { href: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/admin/products', label: 'Products', icon: Package },
  { href: '/admin/orders', label: 'Orders', icon: ShoppingCart },
  { href: '/admin/quotes', label: 'Quote Requests', icon: FileText },
  { href: '/admin/contacts', label: 'Messages', icon: MessageSquare },
];

export const AdminLayout = ({ children, title }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { admin, logout, isAuthenticated, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/admin');
    }
  }, [loading, isAuthenticated, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F7F2]">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="admin-layout">
      {/* Mobile header */}
      <div className="lg:hidden bg-[#1A1A1A] text-white p-4 flex items-center justify-between">
        <h1 className="font-manrope font-bold">
          Kitchen<span className="text-[#8B5E3C]">4U</span> Admin
        </h1>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-sidebar-toggle">
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside 
          className={`admin-sidebar fixed lg:static inset-y-0 left-0 z-50 transform ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0 transition-transform duration-300`}
          data-testid="admin-sidebar"
        >
          <div className="p-6 hidden lg:block">
            <Link to="/" className="font-manrope text-xl font-bold">
              Kitchen<span className="text-[#8B5E3C]">4U</span>
            </Link>
            <p className="text-gray-400 text-sm mt-1">Admin Panel</p>
          </div>

          <nav className="mt-6 lg:mt-0">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`admin-nav-item ${location.pathname === item.href ? 'active' : ''}`}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon size={20} className="mr-3" />
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
            <div className="px-4 py-2 text-sm text-gray-400 mb-2">
              {admin?.name || admin?.email}
            </div>
            <button
              onClick={handleLogout}
              className="admin-nav-item w-full text-red-400 hover:text-red-300"
              data-testid="admin-logout-btn"
            >
              <LogOut size={20} className="mr-3" />
              Sign Out
            </button>
          </div>
        </aside>

        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <main className="flex-1 p-6 lg:p-8" data-testid="admin-main-content">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-[#6B7280] mb-6">
            <Link to="/admin/dashboard" className="hover:text-[#1A1A1A]">Admin</Link>
            <ChevronRight size={14} />
            <span className="text-[#1A1A1A]">{title}</span>
          </div>

          {/* Page title */}
          <h1 className="font-manrope text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-8">
            {title}
          </h1>

          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
